import { SupplierInventoryDeltaResponseDto } from "../types";
import { SupplierPart } from "./partsCalculationUtils";

/**
 * Build the composite key used to look up a delta quantity
 * for a specific supplier + root part family.
 */
export const getSupplierInventoryDeltaKey = (
  supplierId: string,
  rootPartNumber: string,
): string => `${supplierId}::${rootPartNumber}`;

/**
 * Apply consumption deltas to supplier parts.
 *
 * This is the LAST quantity adjustment in the pipeline:
 *   Typesense parts → offline merge → THIS → metrics
 *
 * For each part, the adjusted quantity is:
 *   Math.max(0, familyQuantity - deltaQuantity)
 *
 * Parts without a matching delta entry are returned unchanged.
 */
export const applySupplierInventoryDeltasToParts = <
  T extends SupplierPart,
>({
  supplierId,
  parts,
  supplierInventoryDeltaQuantityMap,
}: {
  supplierId: string;
  parts: T[];
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
}): T[] => {
  if (!supplierInventoryDeltaQuantityMap?.size || !supplierId) return parts;

  return parts.map((part) => {
    const rootPartNumber = part.rootPartNumber;
    if (!rootPartNumber) return part;

    const supplierInventoryDeltaQuantity =
      supplierInventoryDeltaQuantityMap.get(
        getSupplierInventoryDeltaKey(supplierId, rootPartNumber),
      ) ?? 0;

    if (supplierInventoryDeltaQuantity <= 0) return part;

    const familyQuantity = Number(part.familyQuantity) || 0;
    const adjustedFamilyQuantity = Math.max(
      0,
      familyQuantity - supplierInventoryDeltaQuantity,
    );

    return {
      ...part,
      familyQuantity: adjustedFamilyQuantity,
    };
  });
};

/**
 * Build a lookup map from the delta API response.
 *
 * Key:   "supplierId::rootPartNumber"
 * Value: deltaQuantity (number)
 *
 * Returns an empty map if response is undefined or has no results.
 */
export const buildSupplierInventoryDeltaQuantityMap = (
  response: SupplierInventoryDeltaResponseDto | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!response?.results) return map;

  for (const delta of response.results) {
    if (!delta.supplierId || !delta.rootPartNumber) continue;
    map.set(
      getSupplierInventoryDeltaKey(delta.supplierId, delta.rootPartNumber),
      Number(delta.deltaQuantity) || 0,
    );
  }
  return map;
};
